import { useQuery } from '@tanstack/react-query';
import {
    businessApiRoute,
    get as adminGet,
    getBusinessId,
} from '../../../lib/Api';
import axios from 'axios';
import { addDays, endOfMonth, startOfDay } from 'date-fns';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import SelectionBlock from './components/SelectionBlock.jsx';
import ChooseAvailableTime from './components/ChooseAvailableTime.jsx';
import {
    useBookableResourcesQuery,
    useEventTypesQuery,
} from '../../../lib/SharedQueries.js';
import {
    formatDateTime,
    getBrowserTimezone,
    minutesToDuration,
} from '../../../lib/DateTime.js';
import { CheckIcon } from '@heroicons/react/24/outline';
import { getBookWithLabel } from '../../../lib/BookableResourceHelpers.js';
import { isAdminApp } from '../../../public/lib/PageHelpers.js';
import ErrorMessage from '../../ErrorMessage';
import Stepper from '../../Stepper.jsx';
import QuantityPicker from './scheduling/QuantityPicker.jsx';
import SelectBookableResource, {
    getAnyAvailableCalendarId,
} from './scheduling/SelectBookableResource.jsx';
import PickStartTime from './scheduling/PickStartTime.jsx';
import SelectEventType, {
    useQuestionEventTypes,
} from './scheduling/SelectEventType.jsx';
import {
    getSingleEventValue,
    useSchedulingValue,
} from '~/lib/Questions/SchedulingHelpers';
import MonthlyOverview from '~/components/forms/questions/scheduling/MonthlyOverview';
import AddToWaitlistCTA from '~/components/waitlist/AddToWaitlistCTA';
import { conversionTrackStep } from '~/public/lib/ConversionEventTracking';
import useClientTimezone from '~/public/lib/useClientTimezone';
import { FormContext } from '~/components/forms/FormContext';

/**
 *
 * @param value
 * @param setValue
 * @param settings: ignoreEvents, hideBookableResource, hideEventType
 * @returns {JSX.Element}
 * @constructor
 */
const SchedulingQuestion: FC = ({ value, setValue, settings }) => {
    const [hasLoaded, setHasLoaded] = useState(false);
    const clientTz = useClientTimezone();
    const [quantity, setQuantity] = useState(1);
    const [capacity, setCapacity] = useState(1);
    const [defaultTime, setDefaultTime] = useState(null);
    const formContext = useContext(FormContext);

    // If the eventType parameter is set then update the settings to only show that one
    const uri = new URL(document.location.href);
    const directLinkEventType = useMemo(() => {
        return parseInt(uri.searchParams.get('eventType') || '');
    }, []);

    // If the bookableResource parameter is set then update the settings to only show that one
    const directLinkBookableResource = useMemo(() => {
        return parseInt(uri.searchParams.get('bookableResource') || '');
    }, []);

    if (directLinkEventType) {
        settings = { ...settings, eventTypes: [directLinkEventType] };
    }
    if (directLinkBookableResource) {
        settings = {
            ...settings,
            bookableResources: [directLinkBookableResource],
        };
    }

    const [filter, setFilter] = useState({});

    console.log('schedulingdata=', value, settings);

    const singleEventValue = getSingleEventValue(value);

    const { types: allTypes } = useQuestionEventTypes(null, settings);

    const { update } = useSchedulingValue({
        setValue,
        value,
        setQuantity,
        quantity,
        capacity,
        settings,
    });

    useEffect(() => {
        if (singleEventValue?.capacity > 1) {
            setCapacity(singleEventValue?.capacity);
        }
    }, [singleEventValue?.capacity]);

    useEffect(() => {
        // Ensure a client time zone is always set
        if (!singleEventValue?.clientTimezone) {
            update({});
        }
    }, [JSON.stringify(singleEventValue)]);

    useEffect(() => {
        // Is the only thing set startTime? That's the default!
        if (value && value[0] && value[0]?.startTime) {
            setDefaultTime(value[0]?.startTime);
        }
    }, []);

    const selectedEventType = allTypes.find(
        t => t.id === singleEventValue?.eventType,
    );

    useEffect(() => {
        if (!!singleEventValue?.bookableResource) {
            conversionTrackStep('BookableResource', {
                bookableResource: singleEventValue?.bookableResource,
            });
        }
    }, [!!singleEventValue?.bookableResource]);

    useEffect(() => {
        if (!!singleEventValue?.eventType) {
            conversionTrackStep('EventType', {
                eventType: singleEventValue?.eventType,
                eventTypeName: selectedEventType?.name,
            });
        }
    }, [!!singleEventValue?.eventType]);

    useEffect(() => {
        if (!!singleEventValue?.startTime) {
            conversionTrackStep('Time', {
                time: singleEventValue?.startTime,
            });
        }
    }, [!!singleEventValue?.startTime]);

    useEffect(() => {
        if (hasLoaded && !isAdminApp()) {
            // Avoid doing this on initial load
            const lastDiv = document.querySelector(
                '.scheduling-questions-container > div:last-of-type',
            );
            lastDiv?.scrollIntoView({
                block: 'start',
                inline: 'nearest',
                behavior: 'smooth',
            });
        }
    }, [JSON.stringify(value)]);

    const hasMonthlyOverview = settings.stepsOrder === 'monthlyoverviewevent';
    let stepsOrder = settings.stepsOrder || 'event';
    if (stepsOrder === 'monthlyoverviewevent') {
        stepsOrder = 'resourcelast';
    }

    const extraTime = formContext?.extraTime;

    return (
        <div className="scheduling-questions-container">
            {hasMonthlyOverview && (
                <MonthlyOverview
                    settings={settings}
                    setFilter={newFilter => {
                        setFilter(newFilter);
                        // Reset selections
                        update({
                            bookableResource: null,
                            eventType: null,
                        });
                        const lastDiv = document.querySelector(
                            '.scheduling-questions-container > div:last-of-type',
                        );
                        lastDiv?.scrollIntoView({
                            block: 'start',
                            inline: 'nearest',
                            behavior: 'smooth',
                        });
                    }}
                />
            )}
            {(stepsOrder === 'person' || stepsOrder === 'event') && (
                <>
                    {stepsOrder === 'person' && (
                        <>
                            <SelectBookableResource
                                value={singleEventValue?.bookableResource}
                                onChange={newValue =>
                                    update({
                                        bookableResource: newValue,
                                        eventType: null,
                                    })
                                }
                                settings={settings}
                                onChangeCapacity={setCapacity}
                                eventTypeId={null}
                            />

                            {!!singleEventValue?.bookableResource && (
                                <SelectEventType
                                    value={singleEventValue?.eventType}
                                    onChange={(newValue, opt) =>
                                        update({
                                            eventType: newValue,
                                            price: opt?.price,
                                            startTime: defaultTime,
                                        })
                                    }
                                    settings={settings}
                                    bookableResourceId={
                                        singleEventValue?.bookableResource
                                    }
                                    onChangeCapacity={value =>
                                        setCapacity(
                                            value > 1 ? value : capacity,
                                        )
                                    }
                                />
                            )}
                        </>
                    )}
                    {stepsOrder === 'event' && (
                        <>
                            <SelectEventType
                                value={singleEventValue?.eventType}
                                onChange={(newValue, opt) =>
                                    update({
                                        eventType: newValue,
                                        price: opt?.price,
                                        bookableResource: null,
                                    })
                                }
                                settings={settings}
                                onChangeCapacity={setCapacity}
                                bookableResourceId={null}
                            />

                            {!!singleEventValue?.eventType && (
                                <SelectBookableResource
                                    value={singleEventValue?.bookableResource}
                                    onChange={newValue =>
                                        update({
                                            bookableResource: newValue,
                                            startTime: defaultTime,
                                        })
                                    }
                                    onChangeCapacity={value =>
                                        setCapacity(
                                            capacity > 1 ? capacity : value,
                                        )
                                    }
                                    settings={settings}
                                    eventTypeId={singleEventValue?.eventType}
                                />
                            )}
                        </>
                    )}

                    {!!singleEventValue?.eventType &&
                        !!singleEventValue?.bookableResource && (
                            <>
                                <QuantityPicker
                                    value={quantity}
                                    onChange={setQuantity}
                                    capacity={capacity}
                                    settings={settings}
                                />

                                <AddToWaitlistCTA
                                    bookableResource={
                                        singleEventValue?.bookableResource
                                    }
                                    eventType={singleEventValue?.eventType}
                                    settings={settings}
                                />

                                {quantity > 0 && (
                                    <PickStartTime
                                        value={singleEventValue?.startTime}
                                        onChange={newValue => {
                                            if (newValue?.startTime) {
                                                update(newValue);
                                            } else {
                                                update({
                                                    startTime: newValue,
                                                });
                                            }
                                        }}
                                        settings={settings}
                                        eventType={selectedEventType}
                                        quantity={quantity}
                                        bookableResourceId={
                                            singleEventValue?.bookableResource
                                        }
                                        clientTz={clientTz}
                                        initialSelectedDate={filter?.date}
                                        extraTime={extraTime}
                                    />
                                )}
                            </>
                        )}
                </>
            )}
            {stepsOrder === 'resourcelast' && (
                <>
                    <SelectEventType
                        value={singleEventValue?.eventType}
                        onChange={(newValue, opt) =>
                            update({
                                eventType: newValue,
                                price: opt?.price,
                                startTime: defaultTime,
                                bookableResource: null,
                            })
                        }
                        settings={settings}
                        onChangeCapacity={setCapacity}
                        bookableResourceId={null}
                    />

                    {!!singleEventValue?.eventType && (
                        <>
                            <AddToWaitlistCTA
                                bookableResource={null}
                                eventType={singleEventValue?.eventType}
                                settings={settings}
                            />
                            <PickStartTime
                                value={singleEventValue?.startTime}
                                onChange={newValue => {
                                    if (newValue?.startTime) {
                                        update({
                                            ...newValue,
                                            bookableResource: null,
                                        });
                                    } else {
                                        update({
                                            startTime: newValue,
                                            bookableResource: null,
                                        });
                                    }
                                }}
                                settings={settings}
                                eventType={selectedEventType}
                                quantity={quantity}
                                bookableResourceId={getAnyAvailableCalendarId(
                                    settings,
                                )} // Any available
                                clientTz={clientTz}
                                initialSelectedDate={filter?.date}
                                extraTime={extraTime}
                            />
                        </>
                    )}

                    {!!singleEventValue?.eventType &&
                        !!singleEventValue?.startTime && (
                            <SelectBookableResource
                                value={singleEventValue?.bookableResource}
                                onChange={newValue =>
                                    update({ bookableResource: newValue })
                                }
                                onChangeCapacity={value =>
                                    setCapacity(capacity > 1 ? capacity : value)
                                }
                                settings={settings}
                                startTime={singleEventValue?.startTime}
                                eventTypeId={singleEventValue?.eventType}
                            />
                        )}
                </>
            )}
        </div>
    );
};

export default SchedulingQuestion;
